import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { ResolvedSignageConfig } from '@/config/types';

/**
 * The single application store, replacing the legacy Vuex store (`src/store/index.ts`).
 *
 * Field-for-field this is the legacy `MainStoreState` plus `messages` and `transforms`,
 * which absorb what the legacy app did with one dynamically registered Vuex module per
 * widget instance (`store.registerModule(uuid, module)` + a dispatch to
 * `` `${componentId}/messageRecieved` ``). Zustand has no module system, and the domain never
 * needed one: a topic maps to a component id, and a component id maps to its latest payload.
 * That is two records and one optional transform per widget type. See docs/MIGRATION.md §4.
 *
 * ## Reading state outside React
 *
 * The MQTT and preventer engines (SYM-489) live outside the render tree and must not use
 * hooks. They read and write imperatively:
 *
 * ```ts
 * const { baseTopic, topicItems } = useSignageStore.getState();  // snapshot, never reactive
 * useSignageStore.getState().deliverMessage(componentId, payload);
 * const unsubscribe = useSignageStore.subscribe((s) => { ... }); // must be torn down
 * ```
 *
 * `getState()` is a snapshot: it does not subscribe, so anything that has to react to a
 * change either uses a selector hook (inside React) or `subscribe` (outside), and every
 * `subscribe` is unsubscribed in its effect cleanup — displays run for weeks (CLAUDE.md
 * constraint 5).
 *
 * ## Mutable structures in an immutable store
 *
 * `topicItems` is a `Map` and `messages` a plain object, both for parity with the legacy
 * shapes. Every setter writes a *new* `Map`/object; nothing is ever mutated in place, or
 * React would not see the change and the screen would freeze while the data kept flowing.
 */

/** Applied to a raw MQTT payload before it is stored. See `src/store/transforms.ts`. */
export type MessageTransform = (raw: unknown) => unknown;

export type RegisterTopicInput = {
  componentId: string;
  /** Relative to `baseTopic`, exactly as written in the config. */
  topic: string;
  transform?: MessageTransform;
};

export type SignageState = {
  // topic wiring
  /** Absolute topic (`${baseTopic}/${topic}`) -> componentId. One-to-one, see below. */
  topicItems: Map<string, string>;
  /** componentId -> latest payload, post-transform. */
  messages: Record<string, unknown>;
  /** componentId -> transform. Absence *is* the pass-through. */
  transforms: Map<string, MessageTransform>;

  // connection lifecycle
  token: string;
  preventerLoaded: boolean;
  moduleLoaded: boolean;
  canConnectMqtt: boolean;
  mqttConnected: boolean;

  // config-derived
  mqttUrl: string;
  /** ms */
  heartBeat: number;
  baseTopic: string;
  floorCode: string;
};

export type SignageActions = {
  hydrateFromConfig: (config: ResolvedSignageConfig) => void;
  registerTopic: (input: RegisterTopicInput) => void;
  deliverMessage: (componentId: string, raw: unknown) => void;
  setPreventerLoaded: (token: string) => void;
  setModuleLoaded: () => void;
  setCanConnectMqtt: () => void;
  setMqttConnected: (connected: boolean) => void;
  /** Tests only. Production code never resets — a display resets by reloading the page. */
  reset: () => void;
};

export type SignageStore = SignageState & SignageActions;

/**
 * A factory, not a shared constant: the `Map`s and the `messages` object must be fresh per
 * store instance and per `reset()`, or two tests (or a reset and its predecessor) would
 * alias the same containers.
 *
 * Defaults match the legacy store's initial state. `mqttUrl` starts empty rather than at
 * `VITE_MQTT_URL` because `loadConfig()` has already applied that fallback by the time
 * `hydrateFromConfig` runs (docs/MIGRATION.md §2).
 */
function createInitialState(): SignageState {
  return {
    topicItems: new Map<string, string>(),
    messages: {},
    transforms: new Map<string, MessageTransform>(),
    token: '',
    preventerLoaded: false,
    moduleLoaded: false,
    canConnectMqtt: false,
    mqttConnected: false,
    mqttUrl: '',
    heartBeat: 10000,
    baseTopic: '',
    floorCode: 'all',
  };
}

export const useSignageStore = create<SignageStore>()((set, get) => ({
  ...createInitialState(),

  /**
   * Copies the four config-derived fields into the store in one atomic write.
   *
   * The legacy `App.vue` dispatched `updateMqttUrl`, `updateHeartBeat`, `updateBaseTopic`
   * and `updateFloorCode` one by one during `setup()`, which left a window where
   * `baseTopic` was still `''` while widgets were already registering topics — the reason
   * legacy topic keys occasionally came out as `/2/environment-statuses`. One hydrate
   * closes that window. The config is immutable for the page lifetime, so this runs once;
   * calling it twice with the same config is a no-op in effect.
   */
  hydrateFromConfig: (config) => {
    set({
      mqttUrl: config.mqttUrl,
      heartBeat: config.heartBeat,
      baseTopic: config.baseTopic,
      floorCode: config.floorCode,
    });
  },

  /**
   * Claims an absolute topic for a component and stores that component's transform.
   *
   * Legacy (docs/MIGRATION.md §4, bug decision #1) checked the guard against the *relative*
   * topic while keying the map by the *absolute* one, so the guard never matched and a
   * re-registration silently overwrote the entry. Here the absolute key is built once and
   * used for both the guard and the write, which makes the real rule explicit:
   *
   * **First registration wins.** That is the legacy outcome once the guard works, and it
   * keeps the map one-to-one — `topicItems` is `topic -> componentId`, and the MQTT engine
   * routes each incoming message to exactly one component. A second component claiming an
   * already-claimed topic is a config authoring mistake, so it is warned about loudly: the
   * screen will silently show a stale widget otherwise, and only the console says why.
   * Widening the map to `topic -> componentId[]` is a deliberate follow-up, not part of this
   * migration (docs/MIGRATION.md §4, decision #10).
   *
   * The transform is stored regardless of who won the topic, because it is keyed by
   * componentId and costs nothing; the loser simply never receives a message.
   */
  registerTopic: ({ componentId, topic, transform }) => {
    const { baseTopic, topicItems } = get();
    if (!baseTopic) {
      // Not fatal — the key is still consistent between here and the MQTT subscribe, since
      // both read the same store — but it means `hydrateFromConfig` has not run yet and the
      // resulting ACL topic will not match what the broker was provisioned with.
      console.warn(
        '[signage] registerTopic("%s") ran before hydrateFromConfig; baseTopic is still empty',
        topic,
      );
    }

    const absolute = `${baseTopic}/${topic}`;
    const claimedBy = topicItems.get(absolute);
    if (claimedBy !== undefined && claimedBy !== componentId) {
      console.warn(
        '[signage] topic "%s" is already registered by component %s; component %s will ' +
          'receive no messages. Two widgets cannot share one topic — fix the config.',
        absolute,
        claimedBy,
        componentId,
      );
    }

    set((s) => ({
      // First registration wins: only write when the key is unclaimed.
      topicItems: s.topicItems.has(absolute)
        ? s.topicItems
        : new Map(s.topicItems).set(absolute, componentId),
      transforms: transform ? new Map(s.transforms).set(componentId, transform) : s.transforms,
    }));
  },

  /**
   * Stores the latest payload for a component, applying its transform first.
   *
   * A throwing transform drops the message and keeps the last good value on screen. A
   * malformed payload must never blank a widget, let alone the page (CLAUDE.md constraint 4)
   * — the transforms themselves Zod-validate and degrade to an empty result, so a throw here
   * means a bug in a transform, which is worth a console error but not a crash.
   */
  deliverMessage: (componentId, raw) => {
    const transform = get().transforms.get(componentId);
    let value = raw;
    if (transform) {
      try {
        value = transform(raw);
      } catch (error) {
        console.error('[signage] transform failed', componentId, error);
        return;
      }
    }
    set((s) => ({ messages: { ...s.messages, [componentId]: value } }));
  },

  /** The preventer script handed over a token (SYM-489). */
  setPreventerLoaded: (token) => {
    set({ preventerLoaded: true, token });
  },

  /** `ModuleLoader` mounted (SYM-491 wires this; nothing calls it yet). */
  setModuleLoaded: () => {
    set({ moduleLoaded: true });
  },

  /** `savemeta` returned `metaEnabled: true` — the broker will accept us (SYM-489). */
  setCanConnectMqtt: () => {
    set({ canConnectMqtt: true });
  },

  /**
   * Takes a boolean, unlike the legacy one-way `mqttConnected` mutation: SYM-489 flips it
   * back to `false` on `close`/`offline` so the flag reflects reality (docs/MIGRATION.md §7,
   * bug decision #3).
   */
  setMqttConnected: (connected) => {
    set({ mqttConnected: connected });
  },

  reset: () => {
    set(createInitialState());
  },
}));

/* -------------------------------------------------------------------------------------- */
/* Selectors                                                                              */
/*                                                                                        */
/* Always subscribe through one of these. A component that selects the whole store         */
/* re-renders on every MQTT message for every other widget on the screen; with a dozen     */
/* live widgets that is a measurable frame-rate problem on display hardware.               */
/* -------------------------------------------------------------------------------------- */

/**
 * The latest payload for one component, or `undefined` before its first message.
 *
 * `T` is asserted, not validated — validation belongs to the widget's own schema or its
 * transform, which is where a bad payload can be degraded into something renderable.
 *
 * `no-unnecessary-type-parameters` is right that `T` appears once: it is a caller-supplied
 * assertion, not an inference. It is kept because the alternative is an `as` cast at every
 * one of the ~13 widget call sites, which is the same unchecked assertion with more noise.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const useWidgetMessage = <T>(componentId: string): T | undefined =>
  useSignageStore((s) => s.messages[componentId] as T | undefined);

export const useBaseTopic = (): string => useSignageStore((s) => s.baseTopic);
export const useFloorCode = (): string => useSignageStore((s) => s.floorCode);
export const useMqttReady = (): boolean => useSignageStore((s) => s.mqttConnected);

export type ConnectGate = {
  preventerLoaded: boolean;
  moduleLoaded: boolean;
  topicCount: number;
  token: string;
  /** `preventerLoaded && moduleLoaded && topicCount > 0` — the legacy `checkAndConnect`. */
  canConnect: boolean;
};

/**
 * The connect gate, as a plain function so it can be evaluated from outside React and
 * asserted in tests without rendering anything.
 *
 * This is the exact condition that decides whether a display shows live data or a frozen
 * screen (docs/MIGRATION.md §5). All three inputs are asynchronous and complete in any
 * order, which is why it is derived rather than latched by whichever one happens to finish
 * last. `token` rides along because the caller needs it in the same breath; it is
 * deliberately *not* part of `canConnect`, matching the legacy gate, which checked only the
 * three flags — `token` is set atomically with `preventerLoaded` anyway.
 */
export function selectConnectGate(s: SignageState): ConnectGate {
  const topicCount = s.topicItems.size;
  return {
    preventerLoaded: s.preventerLoaded,
    moduleLoaded: s.moduleLoaded,
    topicCount,
    token: s.token,
    canConnect: s.preventerLoaded && s.moduleLoaded && topicCount > 0,
  };
}

/**
 * `useShallow` is required here, and for any selector returning a fresh object: without it
 * the returned identity differs on every render and React re-renders forever.
 */
export const useConnectGate = (): ConnectGate => useSignageStore(useShallow(selectConnectGate));
